import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogPortal } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  isGoogleDriveConfigured,
  requestAccessToken,
  revokeToken,
  pickVideoFiles,
  getUserInfo,
  downloadFile,
  type DriveFile,
  type DriveUser,
} from '@/services/googleDriveService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { uploadService, MAX_CONCURRENT } from '@/services/uploadService';
import { UploadItem } from '@/types/upload';
import { useAuth } from '@/hooks/useAuth';
import {
  Upload,
  Pause,
  Play,
  X,
  RefreshCw,
  Trash2,
  Loader2,
  Check,
  HardDrive,
  ArrowLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import * as DialogPrimitive from "@radix-ui/react-dialog";

interface UploadQueueModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetFolderId?: string | null;
  onPersonalize?: (videoUrl: string, videoName: string) => void;
  hideCloseControls?: boolean;
}

// Limite de tamanho por arquivo do Drive (2GB) — espelha o limite do upload
// normal e protege contra OOM em arquivos enormes carregados em memória.
const DRIVE_MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024;

export function UploadQueueModal({ open, onOpenChange, targetFolderId, onPersonalize, hideCloseControls }: UploadQueueModalProps) {
  useLanguage();
  const { user, plan, hasPermission } = useAuth();
  const canEdit = hasPermission('videos', 'edit');
  const [items, setItems] = useState<UploadItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [hasDeleteConfirmOpen, setHasDeleteConfirmOpen] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  // ── Google Drive state ──
  // 'select' = picker de origem (Computador vs Drive)
  // 'computer' = dropzone tradicional
  // 'drive' = listagem do Drive
  const [source, setSource] = useState<'select' | 'computer' | 'drive'>('select');
  const [driveToken, setDriveToken] = useState<string | null>(null);
  const [driveUser, setDriveUser] = useState<DriveUser | null>(null);
  const [driveConnecting, setDriveConnecting] = useState(false);
  const [driveLoadingFiles, setDriveLoadingFiles] = useState(false);
  const [driveImporting, setDriveImporting] = useState(false);

  useEffect(() => {
    const unsubscribe = uploadService.subscribe(setItems);
    return unsubscribe;
  }, []);

  // Bloquear modal se não tem permissão de edição
  useEffect(() => {
    if (open && !canEdit) {
      onOpenChange(false);
      toast({ title: translateInterfaceText("Sem permissão"), description: translateInterfaceText("Você não tem permissão para fazer upload de vídeos.") });
    }
  }, [open, canEdit, onOpenChange]);

  // Reset do source quando o modal fecha (próxima abertura volta pro picker)
  useEffect(() => {
    if (!open) {
      setSource('select');
    }
  }, [open]);

  const summary = uploadService.getSummary();
  const hasActiveUploads = summary.uploading > 0 || summary.queued > 0;
  const hasPausedUploads = summary.paused > 0;

  // Auto-close modal when all uploads are complete (delay 1.5s for UX)
  useEffect(() => {
    if (items.length === 0) return;

    const allCompleted = items.every(item =>
      item.status === 'done' || item.status === 'error' || item.status === 'canceled'
    );
    const hasCompletedVideos = items.some(item => item.status === 'done');

    // Só auto-fecha se NÃO tiver o modo personalização (onPersonalize)
    if (allCompleted && hasCompletedVideos && !onPersonalize) {
      const timer = setTimeout(() => {
        const doneCount = items.filter(i => i.status === 'done').length;

        // Disparar evento customizado para atualizar a lista de vídeos
        window.dispatchEvent(new CustomEvent('videos-uploaded', {
          detail: { count: doneCount }
        }));

        uploadService.clearCompleted();
        onOpenChange(false);
        toast({
          title: translateInterfaceText("Uploads concluídos"),
          description: translateInterfaceText("{value0} vídeo{value1} importado{value2} com sucesso", { value0: (doneCount), value1: (doneCount !== 1 ? 's' : ''), value2: (doneCount !== 1 ? 's' : '') })
        });
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [items, onPersonalize, onOpenChange]);

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    // Bloqueia usuários sem plano ativo ou com status inativo (status !== 'A')
    const hasActivePlan = !!plan && user?.status === 'A';
    if (!hasActivePlan) {
      toast({
        title: translateInterfaceText("Plano inativo"),
        description: translateInterfaceText("Você precisa de um plano ativo para enviar vídeos. Ative um plano para continuar."),
        variant: 'destructive'
      });
      return;
    }

    const videoFiles = Array.from(files).filter(file =>
      file.type.startsWith('video/')
    );

    if (videoFiles.length === 0) {
      toast({
        title: translateInterfaceText("Formato inválido"),
        description: translateInterfaceText("Selecione apenas arquivos de vídeo"),
        variant: 'destructive'
      });
      return;
    }

    // Bloqueia se exceder o limite simultâneo (uploads ativos + novos)
    const activeCount = items.filter(i =>
      ['uploading', 'queued', 'paused', 'processing'].includes(i.status)
    ).length;
    const availableSlots = MAX_CONCURRENT - activeCount;

    if (videoFiles.length > availableSlots) {
      toast({
        title: translateInterfaceText("Limite de {value0} vídeos por envio", { value0: (MAX_CONCURRENT) }),
        description: availableSlots > 0
          ? translateInterfaceText("Você ainda pode enviar {value0} vídeo{value1} agora. Aguarde os atuais terminarem para enviar mais.", { value0: (availableSlots), value1: (availableSlots !== 1 ? 's' : '') })
          : translateInterfaceText("Aguarde os envios atuais terminarem ou cancele alguns para enviar novos vídeos."),
        variant: 'destructive'
      });
      return;
    }

    uploadService.enqueue(videoFiles, targetFolderId);
    toast({
      title: translateInterfaceText("Vídeos adicionados"),
      description: translateInterfaceText("{value0} vídeo(s) adicionado(s) à fila", { value0: (videoFiles.length) })
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const getGlobalStatusText = () => {
    if (summary.done === summary.total && summary.total > 0) {
      return `${summary.total} vídeo${summary.total !== 1 ? 's' : ''} • ${summary.done} concluído${summary.done !== 1 ? 's' : ''}`;
    }
    if (summary.uploading > 0) {
      const uploadingCount = items.filter(item => 
        item.status === 'uploading' || item.status === 'processing'
      ).length;
      return `Importando ${uploadingCount} ${uploadingCount === 1 ? 'vídeo' : 'vídeos'}...`;
    }
    if (summary.paused > 0) return translateInterfaceText("Envio pausado");
    return translateInterfaceText("Nenhum envio ativo");
  };

  const handleCancelAll = () => {
    if (hasActiveUploads || hasPausedUploads) {
      setShowCancelDialog(true);
    }
  };

  const confirmCancelAll = () => {
    uploadService.cancelAll();
    setShowCancelDialog(false);
  };

  const handleClose = () => {
    const summary = uploadService.getSummary();
    const allFinished = summary.uploading === 0 && 
                        summary.queued === 0 && 
                        summary.paused === 0 &&
                        summary.processing === 0;
    
    if (allFinished && items.length > 0) {
      uploadService.clearCompleted();
    }
    
    onOpenChange(false);
  };

  const handleAddMore = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'video/*';
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      handleFileSelect(target.files);
    };
    input.click();
  };

  // ── Google Drive handlers ──
  // Valida plano/limites, baixa os vídeos do Drive e os adiciona à fila.
  // Recebe os arquivos direto do Picker — sem etapa intermediária de seleção.
  const importDriveFiles = useCallback(async (files: DriveFile[], token: string) => {
    // Mesma validação de plano que o upload normal
    const hasActivePlan = !!plan && user?.status === 'A';
    if (!hasActivePlan) {
      toast({
        title: translateInterfaceText("Plano inativo"),
        description: translateInterfaceText("Você precisa de um plano ativo para enviar vídeos. Ative um plano para continuar."),
        variant: 'destructive',
      });
      return;
    }

    const oversize = files.filter(f => f.size > DRIVE_MAX_FILE_SIZE);
    if (oversize.length > 0) {
      toast({
        title: translateInterfaceText("Arquivo grande demais"),
        description: translateInterfaceText("{value0} arquivo(s) acima do limite de 2GB. Baixe pro PC primeiro.", { value0: (oversize.length) }),
        variant: 'destructive',
      });
      return;
    }

    // Respeita o limite simultâneo do MAX_CONCURRENT (igual upload normal)
    const activeCount = items.filter(i =>
      ['uploading', 'queued', 'paused', 'processing'].includes(i.status)
    ).length;
    const availableSlots = MAX_CONCURRENT - activeCount;
    if (files.length > availableSlots) {
      toast({
        title: translateInterfaceText("Limite de {value0} vídeos por envio", { value0: (MAX_CONCURRENT) }),
        description: availableSlots > 0
          ? translateInterfaceText("Você ainda pode importar {value0} vídeo{value1} agora.", { value0: (availableSlots), value1: (availableSlots !== 1 ? 's' : '') })
          : translateInterfaceText("Aguarde os envios atuais terminarem ou cancele alguns."),
        variant: 'destructive',
      });
      return;
    }

    setDriveImporting(true);
    toast({
      title: translateInterfaceText("Importando do Google Drive"),
      description: translateInterfaceText("Baixando {value0} vídeo{value1}...", { value0: (files.length), value1: (files.length !== 1 ? 's' : '') }),
    });

    try {
      // Baixa um por vez pra não saturar a rede (feedback claro se algum falhar).
      const downloaded: File[] = [];
      for (const drive of files) {
        try {
          const f = await downloadFile(token, drive);
          downloaded.push(f);
        } catch (err) {
          console.warn('[Drive] download error:', drive.name, err);
          toast({
            title: translateInterfaceText("Falha em {value0}", { value0: (drive.name) }),
            description: (err as Error)?.message || translateInterfaceText("Esse vídeo não pôde ser baixado. Os outros continuam."),
            variant: 'destructive',
          });
        }
      }

      if (downloaded.length === 0) {
        toast({
          title: translateInterfaceText("Nenhum vídeo importado"),
          description: translateInterfaceText("Os downloads falharam. Tente novamente."),
          variant: 'destructive',
        });
        return;
      }

      uploadService.enqueue(downloaded, targetFolderId);
      toast({
        title: translateInterfaceText("Vídeos adicionados"),
        description: translateInterfaceText("{value0} vídeo{value1} na fila de envio.", { value0: (downloaded.length), value1: (downloaded.length !== 1 ? 's' : '') }),
      });
    } finally {
      setDriveImporting(false);
    }
  }, [plan, user?.status, items, targetFolderId]);

  // Abre o Google Picker e, ao escolher, já importa os vídeos automaticamente.
  const handleOpenPicker = useCallback(async (token: string) => {
    setDriveLoadingFiles(true);
    let picked: DriveFile[] = [];
    try {
      picked = await pickVideoFiles(token);
    } catch (err) {
      console.warn('[Drive] picker error:', err);
      toast({
        title: translateInterfaceText("Não foi possível abrir o seletor"),
        description: (err as Error)?.message || translateInterfaceText("Tente novamente."),
        variant: 'destructive',
      });
      return;
    } finally {
      setDriveLoadingFiles(false);
    }
    if (picked.length > 0) {
      await importDriveFiles(picked, token);
    }
  }, [importDriveFiles]);

  const handleConnectDrive = useCallback(async () => {
    if (!isGoogleDriveConfigured()) {
      toast({
        title: translateInterfaceText("Integração não configurada"),
        description: translateInterfaceText("Adicione VITE_GOOGLE_CLIENT_ID e VITE_GOOGLE_DEVELOPER_KEY no .env e reinicie o servidor."),
        variant: 'destructive',
      });
      return;
    }
    setDriveConnecting(true);
    try {
      const token = await requestAccessToken();
      setDriveToken(token);
      // Dados do user (não-fatal) só pra exibir "Conectado como ..."
      const userInfo = await getUserInfo(token).catch(() => null);
      if (userInfo) setDriveUser(userInfo);
      // Abre o Picker logo após conectar — é onde o usuário escolhe os vídeos.
      await handleOpenPicker(token);
    } catch (err) {
      const msg = (err as Error)?.message || 'Falha ao conectar';
      console.warn('[Drive] connect error:', err);
      toast({
        title: translateInterfaceText("Não foi possível conectar"),
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setDriveConnecting(false);
    }
  }, [handleOpenPicker]);

  const handleDisconnectDrive = useCallback(async () => {
    if (driveToken) {
      try { await revokeToken(driveToken); } catch { /* noop */ }
    }
    setDriveToken(null);
    setDriveUser(null);
  }, [driveToken]);

  // Calcula progresso geral para o mini bubble
  const activeItems = items.filter(i => i.status === 'uploading' || i.status === 'processing' || i.status === 'queued');
  const totalProgress = activeItems.length > 0
    ? Math.round(activeItems.reduce((acc, i) => acc + i.progress, 0) / activeItems.length)
    : 0;

  const hasItems = items.length > 0;

  return (
    <>
    {/* Mini bubble — exibida quando o modal está fechado mas há uploads ativos */}
    {!open && (hasActiveUploads || items.some(i => i.status === 'processing')) && createPortal(
      <div
        className="fixed bottom-5 left-5 z-[9990] flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer select-none"
        style={{
          background: 'rgba(15,15,20,0.92)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
          minWidth: '220px',
          maxWidth: '280px',
        }}
        onClick={() => onOpenChange(true)}
        title={translateInterfaceText("Clique para ver os uploads")}
      >
        <div className="relative flex-shrink-0 w-8 h-8">
          <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="13" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
            <circle
              cx="16" cy="16" r="13"
              fill="none"
              stroke="#1A73E8"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 13}`}
              strokeDashoffset={`${2 * Math.PI * 13 * (1 - totalProgress / 100)}`}
              style={{ transition: 'stroke-dashoffset 0.4s ease-out' }}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[9px] font-semibold text-white">
            {totalProgress}%
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-white truncate">
            {activeItems.length === 1
              ? activeItems[0].name.replace(/\.[^/.]+$/, '')
              : translateInterfaceText("{value0} vídeos enviando", { value0: (activeItems.length) })}
          </p>
          <p className="text-[11px] text-white/50 mt-0.5">
            {items.some(i => i.status === 'processing')
              ? translateInterfaceText("Processando no servidor...")
              : translateInterfaceText("Enviando para o servidor...")}
          </p>
        </div>
      </div>,
      document.body
    )}

    {/* Enquanto o Google Picker está aberto (driveLoadingFiles), desmontamos
        este modal — o z-index alto dele ficaria na frente do seletor do Google.
        O componente continua montado, então o estado do Drive é preservado. */}
    <Dialog
      open={open && !driveLoadingFiles}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          const summary = uploadService.getSummary();
          const allFinished = summary.uploading === 0 &&
                              summary.queued === 0 &&
                              summary.paused === 0 &&
                              summary.processing === 0;
          if (allFinished && items.length > 0) {
            uploadService.clearCompleted();
          }
        }
        onOpenChange(isOpen);
      }}
    >
      <DialogPortal>
        {/* Overlay */}
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[9998]",
            "bg-[rgba(19,22,28,0.48)] backdrop-blur-[2px]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />

        {/* Modal Content */}
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-[9999] translate-x-[-50%] translate-y-[-50%]",
            "w-[calc(100%-24px)] sm:w-[min(94vw,640px)] md:w-[min(92vw,640px)]",
            "max-h-[80vh]",
            "p-0",
            "rounded-[20px]",
            "bg-white border border-[#E5EAF1]",
            "shadow-[0px_20px_60px_rgba(0,0,0,0.18)]",
            "flex flex-col overflow-hidden",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "transition-all duration-[250ms] ease-out"
          )}
        >
          {/* ─── Header ─── */}
          <div className="flex-shrink-0 px-6 pt-6 pb-4 flex items-start justify-between">
            <div className="flex items-start gap-3">
              {/* Botão voltar — só aparece na tela Drive/Computer quando ainda
                  não há uploads na fila (durante upload, voltar não faz sentido) */}
              {!hasItems && source !== 'select' && (
                <button
                  onClick={() => setSource('select')}
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-[#6B7688] hover:bg-[#F3F4F6] hover:text-[#1A1A1A] transition-colors flex-shrink-0 -ml-1"
                  aria-label={translateInterfaceText("Voltar")}
                >
                  <ArrowLeft className="h-[18px] w-[18px]" strokeWidth={2} />
                </button>
              )}
              <div className="w-10 h-10 rounded-xl bg-[#F0F4FA] flex items-center justify-center flex-shrink-0">
                <Upload className="h-5 w-5 text-[#7BA3E8]" strokeWidth={1.8} />
              </div>
              <div>
                <DialogPrimitive.Title className="text-[17px] font-semibold text-[#1A1A1A] leading-tight">
                  {!hasItems && source === 'drive' ? translateInterfaceText("Importar do Drive") : translateInterfaceText("Importar vídeos")}
                </DialogPrimitive.Title>
                <p className="text-[13px] text-[#8C95A6] mt-1.5 leading-snug">
                  {hasItems
                    ? (summary.done === summary.total && summary.total > 0
                        ? translateInterfaceText("{value0} vídeo{value1} importado{value2} com sucesso", { value0: (summary.total), value1: (summary.total !== 1 ? 's' : ''), value2: (summary.total !== 1 ? 's' : '') })
                        : getGlobalStatusText())
                    : source === 'drive'
                      ? translateInterfaceText("Selecione vídeos da sua conta Google Drive.")
                      : source === 'computer'
                        ? translateInterfaceText("Arraste e solte ou escolha arquivos do seu computador.")
                        : translateInterfaceText("Escolha a origem dos vídeos para começar.")
                  }
                </p>
              </div>
            </div>
            <DialogPrimitive.Close className="w-10 h-10 rounded-xl flex items-center justify-center text-[#6B7688] hover:bg-[#F3F4F6] hover:text-[#1A1A1A] transition-colors flex-shrink-0 -mt-1 -mr-1">
              <X className="h-5 w-5" strokeWidth={2} />
            </DialogPrimitive.Close>
          </div>

          {/* ─── Content ─── */}
          <div className="flex-1 overflow-hidden px-6 pb-6 flex flex-col">
            {!hasItems && source === 'select' ? (
              /* ═══ STEP 0 — Source picker (Computador vs Drive) ═══ */
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Card Computador */}
                <button
                  onClick={() => setSource('computer')}
                  className="group text-left rounded-[18px] p-[22px] min-h-[190px] flex flex-col justify-between bg-white border border-[#E5EAF1] hover:border-[#2563eb]/40 hover:-translate-y-0.5 hover:shadow-[0_16px_38px_rgba(15,23,42,0.10)] transition-all duration-200"
                >
                  <div>
                    <div className="w-[42px] h-[42px] rounded-[14px] bg-[#EEF2FF] border border-[#DBE4FF] flex items-center justify-center mb-4">
                      <HardDrive className="h-5 w-5 text-[#2563eb]" strokeWidth={1.8} />
                    </div>
                    <h3 className="text-[15px] font-bold text-[#1A1A1A] mb-1 tracking-[-0.01em]">{translateInterfaceText("Upload do computador")}</h3>
                    <p className="text-[13px] text-[#6B7688] leading-[1.5] max-w-[240px]">
                      {translateInterfaceText("Envie vídeos do seu computador com upload rápido e seguro.")}</p>
                  </div>
                  <span className="mt-4 inline-flex items-center justify-center h-9 px-4 rounded-[12px] text-[13px] font-medium text-white bg-[linear-gradient(180deg,#2563eb_0%,#1d4ed8_100%)] w-fit">
                    {translateInterfaceText("Selecionar vídeos")}</span>
                </button>

                {/* Card Google Drive */}
                <button
                  onClick={() => setSource('drive')}
                  className="group text-left rounded-[18px] p-[22px] min-h-[190px] flex flex-col justify-between bg-white border border-[#E5EAF1] hover:border-[#2563eb]/40 hover:-translate-y-0.5 hover:shadow-[0_16px_38px_rgba(15,23,42,0.10)] transition-all duration-200"
                >
                  <div>
                    <div className="w-[42px] h-[42px] rounded-[14px] bg-[#F8FAFC] border border-[#E5EAF1] flex items-center justify-center mb-4">
                      <GoogleDriveIcon className="h-5 w-5" />
                    </div>
                    <h3 className="text-[15px] font-bold text-[#1A1A1A] mb-1 tracking-[-0.01em]">{translateInterfaceText("Google Drive")}</h3>
                    <p className="text-[13px] text-[#6B7688] leading-[1.5] max-w-[240px]">
                      {translateInterfaceText("Importe vídeos direto da sua conta Google Drive.")}</p>
                  </div>
                  <span className="mt-4 inline-flex items-center justify-center h-9 px-4 rounded-[12px] text-[13px] font-medium text-[#1A1A1A] bg-[#F8FAFC] border border-[#E5EAF1] group-hover:bg-[#EEF2FF] group-hover:border-[#DBE4FF] group-hover:text-[#2563eb] transition-colors w-fit">
                    {translateInterfaceText("Conectar Google Drive")}</span>
                </button>
              </div>
            ) : !hasItems && source === 'computer' ? (
              /* ═══ STEP 1A — Computer dropzone ═══ */
              <div
                role="button"
                tabIndex={0}
                aria-label={translateInterfaceText("Selecionar vídeos para upload")}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={handleAddMore}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAddMore(); }}
                className={cn(
                  "min-h-[254px] max-h-[310px] border-[1px] border-dashed rounded-2xl flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-150 px-6 py-6",
                  "max-sm:min-h-[200px] max-sm:max-h-[260px]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4B7BFF]/30 focus-visible:ring-offset-2",
                  isDragging
                    ? "border-[#2563eb] border-solid bg-[#EFF4FF] shadow-[0_0_0_3px_rgba(37,99,235,0.07)]"
                    : "border-[rgba(0,0,0,0.12)] bg-[#FAFBFD] hover:border-[rgba(0,0,0,0.18)]"
                )}
              >
                <div className={cn(
                  "transition-transform duration-300",
                  isDragging ? "scale-[1.05]" : "animate-[pulse-subtle_3s_ease-in-out_infinite]"
                )}>
                  <Upload className={cn(
                    "mx-auto h-10 w-10 mb-4 transition-colors duration-200",
                    isDragging ? "text-[#2563eb]" : "text-[#93a3bc]"
                  )} strokeWidth={1.4} />
                </div>
                <p className="text-[15px] font-semibold text-[#1A1A1A] mb-1">
                  {translateInterfaceText("Arraste vídeos aqui")}</p>
                <p className="text-[13px] text-[#6B7688] mb-5">
                  {translateInterfaceText("ou clique no botão abaixo para selecionar")}</p>
                <span className="inline-flex items-center justify-center h-9 px-5 rounded-lg text-[14px] font-medium bg-[#2563eb] hover:bg-[#2358d4] text-white pointer-events-none select-none transition-colors duration-150">
                  {translateInterfaceText("Selecionar vídeos")}</span>
              </div>
            ) : !hasItems && source === 'drive' ? (
              /* ═══ STEP 1B — Google Drive ═══ */
              !driveToken ? (
                <div className="min-h-[280px] rounded-[18px] border border-[#E5EAF1] bg-[#FAFBFD] flex flex-col items-center justify-center text-center px-6 py-8">
                  <div className="w-14 h-14 rounded-2xl bg-white border border-[#E5EAF1] flex items-center justify-center mb-4">
                    <GoogleDriveIcon className="h-7 w-7" />
                  </div>
                  <p className="text-[15px] font-semibold text-[#1A1A1A] mb-1">
                    {translateInterfaceText("Conecte sua conta Google")}</p>
                  <p className="text-[13px] text-[#6B7688] mb-5 max-w-[340px]">
                    {translateInterfaceText("Você poderá escolher arquivos da sua conta Google com segurança.")}</p>
                  <button
                    onClick={handleConnectDrive}
                    disabled={driveConnecting}
                    className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-[12px] text-[14px] font-medium text-white bg-[linear-gradient(180deg,#2563eb_0%,#1d4ed8_100%)] disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {driveConnecting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {driveConnecting ? translateInterfaceText("Conectando...") : translateInterfaceText("Conectar Google Drive")}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col flex-1 min-h-0">
                  {/* Cabeçalho conectado */}
                  <div className="flex items-center justify-between mb-3 rounded-[12px] border border-[#E5EAF1] bg-[#FAFBFD] px-3.5 py-2.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <GoogleDriveIcon className="h-4 w-4 flex-shrink-0" />
                      <span className="text-[12.5px] text-[#6B7688] truncate">
                        {translateInterfaceText("Conectado como")}{" "}<span className="text-[#1A1A1A] font-medium">{driveUser?.email || translateInterfaceText("conta Google")}</span>
                      </span>
                    </div>
                    <button
                      onClick={handleDisconnectDrive}
                      disabled={driveImporting}
                      className="text-[12px] font-medium text-[#2563eb] hover:text-[#1d4ed8] transition-colors flex-shrink-0 ml-3 disabled:opacity-50"
                    >
                      {translateInterfaceText("Desconectar")}</button>
                  </div>

                  {/* Seleção pelo Google Picker — importa automaticamente ao escolher */}
                  <div className="flex-1 min-h-[240px] rounded-[14px] border border-[#E5EAF1] bg-white flex flex-col items-center justify-center text-center px-6 py-10">
                    {driveImporting ? (
                      <>
                        <Loader2 className="h-6 w-6 animate-spin text-[#2563eb] mb-3" />
                        <p className="text-[13px] text-[#6B7688]">
                          {translateInterfaceText("Baixando seus vídeos do Google Drive...")}</p>
                      </>
                    ) : (
                      <>
                        <div className="w-12 h-12 rounded-2xl bg-[#EEF2FF] border border-[#DBE4FF] flex items-center justify-center mb-3">
                          <GoogleDriveIcon className="h-5 w-5" />
                        </div>
                        <p className="text-[13.5px] font-semibold text-[#1A1A1A] mb-1">{translateInterfaceText("Escolha os vídeos no Google Drive")}</p>
                        <p className="text-[12px] text-[#6B7688] max-w-[300px] mb-4">
                          {translateInterfaceText("Abra o seletor do Google e marque os vídeos. Eles entram direto na fila de envio.")}</p>
                        <button
                          onClick={() => driveToken && handleOpenPicker(driveToken)}
                          className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-[12px] text-[13px] font-medium text-white bg-[linear-gradient(180deg,#2563eb_0%,#1d4ed8_100%)]"
                        >
                          {translateInterfaceText("Escolher vídeos no Drive")}</button>
                      </>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end mt-4 -mx-6 px-[22px] py-4 border-t border-[#E5EAF1] bg-white">
                    <button
                      onClick={() => setSource('select')}
                      disabled={driveImporting}
                      className="text-[13px] font-medium text-[#6B7688] hover:text-[#1A1A1A] transition-colors px-2 h-9 disabled:opacity-50"
                    >
                      {translateInterfaceText("Voltar")}</button>
                  </div>
                </div>
              )
            ) : (
              /* Estado IMPORTANDO/CONCLUÍDO — grid com footer bar */
              <div className="flex flex-col flex-1 min-h-0">
                {/* Grid de cards — área scrollável */}
                <div className="flex-1 min-h-[220px] max-h-[400px] overflow-y-auto -mx-1 px-1"
                  style={{ scrollbarWidth: 'thin', scrollbarColor: '#1A73E8 rgba(0,0,0,0.05)' }}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 p-1">
                    {items.map(item => (
                      <UploadItemCard
                        key={item.id}
                        item={item}
                        onDeleteConfirmChange={setHasDeleteConfirmOpen}
                        isSelected={selectedVideoId === item.id}
                        onSelect={() => {
                          if (item.status === 'done') {
                            setSelectedVideoId(selectedVideoId === item.id ? null : item.id);
                          }
                        }}
                        selectionMode={!!onPersonalize}
                      />
                    ))}
                  </div>
                </div>

                {/* Footer bar com divisor */}
                <div className="flex-shrink-0 pt-3 mt-3 border-t border-[#ECEEF2] flex items-center justify-between">
                  <span className="text-[13px] font-medium text-[#6B7688]">
                    {summary.done === summary.total && summary.total > 0
                      ? translateInterfaceText("{value0} vídeo{value1} importado{value2}", { value0: (summary.total), value1: (summary.total !== 1 ? 's' : ''), value2: (summary.total !== 1 ? 's' : '') })
                      : translateInterfaceText("Importando {value0} vídeo{value1}...", { value0: (items.length), value1: (items.length !== 1 ? 's' : '') })
                    }
                  </span>
                  <div className="flex items-center gap-4">
                    {(hasActiveUploads || hasPausedUploads) && (
                      <button
                        onClick={handleCancelAll}
                        className="text-[13px] font-medium text-[#6B7688]/80 hover:text-[#6B7688] hover:underline transition-colors"
                      >
                        {translateInterfaceText("Cancelar")}</button>
                    )}
                    <button
                      onClick={handleAddMore}
                      className="text-[13px] font-medium text-[#1A73E8] hover:text-[#1557b0] transition-colors"
                    >
                      {translateInterfaceText("+ Adicionar mais")}</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ─── Footer personalizar (apenas no modo onPersonalize) ─── */}
          {onPersonalize && items.some(item => item.status === 'done') && (
            <div className="flex-shrink-0 px-6 py-4 border-t border-[#ECEEF2] flex items-center justify-end">
              <Button
                onClick={() => {
                  const selectedItem = items.find(i => i.id === selectedVideoId && i.status === 'done');
                  if (selectedItem) {
                    const videoUrl = URL.createObjectURL(selectedItem.file);
                    onPersonalize(videoUrl, selectedItem.name);
                    onOpenChange(false);
                  }
                }}
                disabled={!selectedVideoId || !items.find(i => i.id === selectedVideoId && i.status === 'done')}
                className={cn(
                  "bg-[#1A73E8] text-white font-medium rounded-xl px-5 h-[40px] text-[13px]",
                  "hover:bg-[#1A73E8] hover:shadow-[0_2px_8px_rgba(26,115,232,0.25)]",
                  "disabled:opacity-50 transition-shadow duration-200"
                )}
              >
                {translateInterfaceText("Personalizar Vídeo")}</Button>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>

      {/* Modal de Confirmação de Cancelamento */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent className="z-[10010] rounded-2xl w-[92vw] sm:w-[400px] sm:max-w-[400px]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translateInterfaceText("Cancelar o envio de")}{" "}{items.filter(i => ['uploading', 'queued', 'paused'].includes(i.status)).length} {" "}{translateInterfaceText("vídeo")}{items.filter(i => ['uploading', 'queued', 'paused'].includes(i.status)).length !== 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translateInterfaceText("Os uploads em andamento serão cancelados. Esta ação não pode ser desfeita.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="hover:bg-gray-50">{translateInterfaceText("Continuar enviando")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancelAll} className="bg-red-600 hover:bg-red-700">
              {translateInterfaceText("Cancelar uploads")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
    </>
  );
}

/* Ícone do Google Drive (SVG branded — copiado do dopamine, neutro). */
function GoogleDriveIcon({ className }: { className?: string }) {
  useLanguage();
  return (
    <svg viewBox="0 0 87.3 78" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3L27.5 53H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#7EAAFF" />
      <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 48.5C.4 49.9 0 51.45 0 53h27.5l16.15-28z" fill="#A9C5FF" />
      <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.5l5.85 11.5 7.9 12.3z" fill="#7EAAFF" />
      <path d="M43.65 25l13.75-23.8C56.05.4 54.5 0 52.9 0H34.4c-1.55 0-3.1.45-4.5 1.2L43.65 25z" fill="#5A8CFF" />
      <path d="M59.8 53H27.5l-13.75 23.8c1.4.8 2.95 1.2 4.5 1.2h50.8c1.55 0 3.1-.45 4.5-1.2L59.8 53z" fill="#A9C5FF" />
      <path d="M73.4 26.5L60.75 4.5c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5L73.4 26.5z" fill="#5A8CFF" />
    </svg>
  );
}

interface UploadItemCardProps {
  item: UploadItem;
  onDeleteConfirmChange?: (open: boolean) => void;
  isSelected?: boolean;
  onSelect?: () => void;
  selectionMode?: boolean;
}

function UploadItemCard({ item, onDeleteConfirmChange, isSelected, onSelect, selectionMode }: UploadItemCardProps) {
  useLanguage();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const handleDeleteConfirmChange = (open: boolean) => {
    setShowDeleteConfirm(open);
    onDeleteConfirmChange?.(open);
  };
  
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const isCompleted = item.status === 'done';
  const isError = item.status === 'error';
  const isUploading = ['uploading', 'processing'].includes(item.status);
  const isSelectable = selectionMode && isCompleted;

  return (
    <div 
      className={cn(
        "group relative rounded-[14px] overflow-hidden transition-all duration-300 animate-fadeInScale",
        "border border-[rgba(255,255,255,0.22)]",
        "shadow-[0_2px_12px_rgba(0,0,0,0.08)]",
        "h-[148px] bg-white",
        isSelectable && !isSelected && "cursor-pointer hover:scale-[1.02] hover:shadow-[0_0_0_2px_rgba(59,130,246,0.2)]",
        isSelected && "border-2 border-[#3B82F6] shadow-[0_4px_14px_rgba(0,0,0,0.12)]",
        !isSelectable && isCompleted && "opacity-90"
      )}
      onClick={isSelectable ? onSelect : undefined}
    >
      
      {/* Thumbnail area - full height */}
      <div className="relative w-full h-full bg-[#F5F6F8] overflow-hidden">
        {item.thumbnail && item.thumbnail !== '/placeholder.svg' ? (
          <div className="relative w-full h-full">
            {/* CAMADA 1: Imagem base (sempre 100% visível) */}
            <img
              src={item.thumbnail}
              alt={item.name}
              className="w-full h-full object-cover object-center absolute inset-0"
              style={{ 
                borderRadius: 'inherit',
                imageRendering: 'auto'
              }}
            />
            
            {/* CAMADA 2: Máscara progressiva premium (diminui da direita para esquerda) */}
            {isUploading && (
              <div 
                className="absolute top-0 bottom-0 z-[1] pointer-events-none"
                style={{
                  width: `${100 - item.progress}%`,
                  right: 0,
                  left: 'auto',
                  background: 'linear-gradient(to right, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.75) 100%)',
                  backdropFilter: 'blur(4px)',
                  transition: 'width 0.35s ease-out',
                }}
              />
            )}
          </div>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#F9FBFF] to-[#E5EAF1] flex items-center justify-center">
            <Upload className="h-10 w-10 text-[#6B7688] opacity-30" />
          </div>
        )}
        
        {/* Botão Remover (X) - canto superior direito - estilo VTurb premium */}
        {isUploading && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              uploadService.cancel(item.id);
            }}
            className="absolute top-[6px] right-[6px] z-[5] w-[26px] h-[26px] rounded-full flex items-center justify-center transition-all duration-200"
            style={{
              background: 'rgba(0, 0, 0, 0.55)',
              backdropFilter: 'blur(4px)',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.75)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.55)'}
            title={translateInterfaceText("Remover")}
          >
            <X className="h-3 w-3 text-white" strokeWidth={2.5} />
          </button>
        )}
        
        {/* Botão Pause/Play - centralizado - estilo VTurb premium 44x44 circular */}
        {(item.status === 'uploading' || item.status === 'paused') && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              item.status === 'uploading' 
                ? uploadService.pause(item.id) 
                : uploadService.resume(item.id);
            }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[5] w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200"
            style={{
              background: 'rgba(0, 0, 0, 0.65)',
              backdropFilter: 'blur(6px)',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.65)'}
            title={item.status === 'uploading' ? translateInterfaceText("Pausar") : translateInterfaceText("Retomar")}
          >
            {item.status === 'uploading' 
              ? <Pause className="h-[18px] w-[18px] text-white" strokeWidth={2.5} />
              : <Play className="h-[18px] w-[18px] text-white ml-0.5" strokeWidth={2.5} />
            }
          </button>
        )}
        
        {/* Processing Spinner - centralizado */}
        {item.status === 'processing' && (
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[5] w-10 h-10 rounded-lg flex items-center justify-center"
            style={{
              background: 'rgba(0, 0, 0, 0.55)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Loader2 className="h-5 w-5 animate-spin text-white" />
          </div>
        )}
        
        {/* Porcentagem na thumbnail - badge glassmorphism premium */}
        {isUploading && (
          <div 
            className="absolute bottom-2 right-2 z-[5] px-2 py-1 rounded-lg text-[12px] font-medium text-white"
            style={{
              background: 'rgba(0, 0, 0, 0.55)',
              backdropFilter: 'blur(3px)',
            }}
          >
            {item.progress}%
          </div>
        )}
        
        {/* Check de seleção premium minimalista - só aparece quando selecionado */}
        {isSelected && isCompleted && (
          <div 
            className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center z-[6] transition-all duration-200 animate-in zoom-in-95 bg-white border-2 border-[#3B82F6] shadow-[0_2px_6px_rgba(0,0,0,0.15)]"
          >
            <Check className="w-3 h-3 text-[#3B82F6]" strokeWidth={3} />
          </div>
        )}
        
        {/* Badge de Erro — clicável para remover */}
        {isError && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteConfirmChange(true);
            }}
            className="absolute top-2 right-2 w-7 h-7 bg-[#FDE8E8] hover:bg-[#FECACA] rounded-full flex items-center justify-center shadow-sm z-[6] transition-colors duration-150"
            title={translateInterfaceText("Remover")}
          >
            <X className="w-3.5 h-3.5 text-[#D13636]" strokeWidth={2.5} />
          </button>
        )}

        {/* Overlay de seleção premium - escuro suave */}
        {isSelected && isCompleted && (
          <div
            className="absolute inset-0 z-[1] pointer-events-none rounded-[inherit]"
            style={{
              background: 'rgba(0, 0, 0, 0.25)',
              backdropFilter: 'blur(2px)',
            }}
          />
        )}

        {/* Nome do vídeo overlaid - gradiente na base */}
        <div
          className="absolute bottom-0 left-0 right-0 z-[4] px-3 py-2.5 pointer-events-none"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 100%)',
            borderRadius: '0 0 14px 14px',
          }}
        >
          <h4
            className="font-medium text-[12px] text-white truncate"
            title={item.name}
          >
            {item.name}
          </h4>
        </div>

        {/* Botão Excluir (hover) - para vídeos concluídos/cancelados */}
        {['done', 'canceled'].includes(item.status) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteConfirmChange(true);
            }}
            className="absolute bottom-2.5 right-2.5 w-7 h-7 bg-white/95 hover:bg-white hover:scale-105 rounded-lg flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-all duration-200"
            title={translateInterfaceText("Remover")}
          >
            <Trash2 className="h-3.5 w-3.5 text-[#D13636]" />
          </button>
        )}
      </div>

      {/* Modal de Confirmação de Remoção - Premium Nested */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={handleDeleteConfirmChange}>
        <AlertDialogContent 
          className={cn(
            "z-[10002] w-[92vw] sm:w-[400px] sm:max-w-[400px]",
            "rounded-[16px] border-0 bg-white",
            "shadow-[0_12px_32px_rgba(0,0,0,0.22)]",
            "animate-[scaleIn_0.18s_ease-out]"
          )}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{translateInterfaceText("Remover vídeo da fila?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {translateInterfaceText("O vídeo \"")}{item.name}{translateInterfaceText("\" será removido da fila de upload.")}{item.status === 'done' && translateInterfaceText(" Esta ação não afeta o vídeo já enviado.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-muted-foreground hover:bg-muted hover:text-[#64748b] transition-colors">{translateInterfaceText("Voltar")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                uploadService.remove(item.id);
                handleDeleteConfirmChange(false);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {translateInterfaceText("Remover")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
